import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Branch, CreateBranchInput, UpdateBranchInput } from '@/queries/useBranches'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

interface BranchFormProps {
  branch?: Branch | null
  onSubmit: (data: CreateBranchInput | UpdateBranchInput) => void | Promise<void>
  isLoading?: boolean
  error?: Error | null
  onCancel?: () => void
}

export default function BranchForm({
  branch,
  onSubmit,
  isLoading = false,
  error,
  onCancel,
}: BranchFormProps) {
  const { t } = useTranslation('admin')
  
  // Create schema with translated error message
  const branchSchema = z.object({
    name: z.string().min(1, t('branches.form.nameRequired')),
    address: z.string().optional(),
  })

  type BranchFormData = z.infer<typeof branchSchema>

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<BranchFormData>({
    resolver: zodResolver(branchSchema),
    defaultValues: {
      name: branch?.name || '',
      address: branch?.address || '',
    },
  })

  const handleFormSubmit = async (data: BranchFormData) => {
    // Convert empty string to undefined for optional fields
    const submitData: CreateBranchInput | UpdateBranchInput = {
      name: data.name,
      address: data.address && data.address.trim() !== '' ? data.address : undefined,
    }
    await onSubmit(submitData)
  }

  return (
    <form 
      onSubmit={handleSubmit(handleFormSubmit)} 
      className="flex flex-col gap-[var(--spacing-md)]"
    >
      {error && (
        <Alert variant="destructive" data-testid="branch-form-error">
          <AlertDescription>
            {error.message || t('branches.form.errorOccurred')}
          </AlertDescription>
        </Alert>
      )}

      <div className="flex flex-col gap-[var(--spacing-sm)]">
        <Label htmlFor="name" data-testid="branch-form-name-label">
          {t('branches.form.name')} <span className="text-destructive">*</span>
        </Label>
        <Input
          id="name"
          {...register('name')}
          placeholder={t('branches.form.namePlaceholder')}
          data-testid="branch-form-name-input"
          className={errors.name ? 'border-destructive' : ''}
        />
        {errors.name && (
          <p
            className="text-[0.8125rem] text-[var(--error)] mt-[var(--space-1)]"
            data-testid="branch-form-name-error"
          >
            {errors.name.message}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-[var(--spacing-sm)]">
        <Label htmlFor="address" data-testid="branch-form-address-label">
          {t('branches.form.address')}
        </Label>
        <Input
          id="address"
          {...register('address')}
          placeholder={t('branches.form.addressPlaceholder')}
          data-testid="branch-form-address-input"
          className={errors.address ? 'border-destructive' : ''}
        />
        {errors.address && (
          <p
            className="text-[0.8125rem] text-[var(--error)] mt-[var(--space-1)]"
            data-testid="branch-form-address-error"
          >
            {errors.address.message}
          </p>
        )}
      </div>

      <div className="flex justify-end gap-[var(--spacing-sm)] rtl:justify-start pt-[var(--spacing-md)]">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isSubmitting || isLoading}
            data-testid="branch-form-cancel-button"
          >
            {t('branches.form.cancel')}
          </Button>
        )}
        <Button
          type="submit"
          disabled={isSubmitting || isLoading}
          data-testid="branch-form-submit-button"
        >
          {isSubmitting || isLoading
            ? t('branches.form.saving')
            : branch
            ? t('branches.form.update')
            : t('branches.form.create')}
        </Button>
      </div>
    </form>
  )
}

